/**
 * Integrations API Service
 * Handles all integration-related API calls (providers, settings, and sync)
 */

import { apiClient } from "./client";

/**
 * Integration Providers API (Global Admin only)
 */
const integrationsApi = {
  // ========================================
  // INTEGRATION PROVIDERS (Global Admin)
  // ========================================

  /**
   * Get all integration providers
   * @param {Object} params - Query parameters
   * @param {number} params.type - Filter by integration type (0=Hr, 1=Till)
   * @param {boolean} params.activeOnly - Filter for active providers only
   * @returns {Promise} API response with providers list
   */
  async getProviders(params = {}) {
    return apiClient.get("/integrations/providers", params);
  },

  /**
   * Get a specific integration provider by ID
   * @param {string} id - Provider ID
   * @returns {Promise} API response with provider details
   */
  async getProvider(id) {
    return apiClient.get(`/integrations/providers/${id}`);
  },

  /**
   * Create a new integration provider (Global Admin only)
   * @param {Object} data - Provider data
   * @param {number} data.type - Integration type (0=Hr, 1=Till)
   * @param {string} data.name - Provider name
   * @param {string} data.code - Unique code
   * @param {string} data.logoUrl - Logo URL
   * @param {boolean} data.isActive - Active status
   * @param {string} data.defaultConfiguration - JSON configuration
   * @returns {Promise} API response with created provider
   */
  async createProvider(data) {
    return apiClient.post("/integrations/providers", data);
  },

  /**
   * Update an integration provider (Global Admin only)
   * @param {string} id - Provider ID
   * @param {Object} data - Updated provider data
   * @returns {Promise} API response with updated provider
   */
  async updateProvider(id, data) {
    return apiClient.put(`/integrations/providers/${id}`, data);
  },

  /**
   * Delete an integration provider (Global Admin only)
   * @param {string} id - Provider ID
   * @returns {Promise} API response
   */
  async deleteProvider(id) {
    return apiClient.delete(`/integrations/providers/${id}`);
  },

  // ========================================
  // INTEGRATION SETTINGS (Tenant Admin)
  // ========================================

  /**
   * Get all integration settings for the current tenant
   * @param {Object} params - Query parameters
   * @param {string} params.tenantId - Tenant ID (Global Admin only)
   * @param {number} params.type - Filter by integration type
   * @returns {Promise} API response with settings list
   */
  async getSettings(params = {}) {
    return apiClient.get("/integrations/settings", params);
  },

  /**
   * Get integration settings with available providers for a tenant
   * @param {Object} params - Query parameters
   * @param {string} params.tenantId - Tenant ID (Global Admin only)
   * @returns {Promise} API response with overview
   */
  async getSettingsOverview(params = {}) {
    return apiClient.get("/integrations/settings/available", params);
  },

  /**
   * Get integration status for a tenant (which providers are configured)
   * @param {string} tenantId - Tenant ID (optional, uses current tenant)
   * @returns {Promise} API response with { hrProvider, posProvider, hasHrIntegration, hasPosIntegration, lastHrSync, lastTillSync }
   */
  async getStatus(tenantId = null) {
    const params = tenantId ? { tenantId } : {};
    return apiClient.get("/integrations/status", params);
  },

  /**
   * Save integration settings for a tenant
   * @param {Object} data - Settings data
   * @param {string} data.tenantId - Tenant ID (optional, uses current tenant)
   * @param {string} data.integrationProviderId - Provider ID
   * @param {boolean} data.isEnabled - Enable/disable integration
   * @param {string} data.apiKey - API key
   * @param {string} data.baseUrl - Base URL
   * @param {string} data.username - Username
   * @param {string} data.password - Password
   * @param {string} data.additionalConfiguration - Additional JSON config
   * @returns {Promise} API response with saved settings
   */
  async saveSettings(data) {
    return apiClient.post("/integrations/settings", data);
  },

  /**
   * Delete integration settings
   * @param {string} id - Settings ID
   * @returns {Promise} API response
   */
  async deleteSettings(id) {
    return apiClient.delete(`/integrations/settings/${id}`);
  },

  /**
   * Test integration connection
   * @param {string} id - Settings ID
   * @returns {Promise} API response with test result
   */
  async testConnection(id) {
    return apiClient.post(`/integrations/settings/${id}/test`);
  },

  // ========================================
  // SYNC OPERATIONS
  // ========================================

  /**
   * Quick sync - syncs clock records and TRONC records for the last 7 days.
   * Called by the status bar sync button.
   * @param {string} tenantId - Tenant ID (optional, uses current tenant)
   * @returns {Promise} API response with quick sync result
   */
  async quickSync(tenantId = null) {
    const params = tenantId ? { tenantId } : {};
    return apiClient.post("/integrations/sync/quick", null, { params });
  },

  /**
   * Sync locations from HR provider.
   * Only creates new locations if they don't exist by name.
   * @param {string} tenantId - Tenant ID (optional, uses current tenant)
   * @returns {Promise} API response with sync result
   */
  async syncLocations(tenantId = null) {
    const params = tenantId ? { tenantId } : {};
    return apiClient.post("/integrations/sync/locations", null, { params });
  },

  /**
   * Sync job roles from HR provider.
   * New roles are created with default settings.
   * @param {string} tenantId - Tenant ID (optional, uses current tenant)
   * @returns {Promise} API response with sync result
   */
  async syncJobRoles(tenantId = null) {
    const params = tenantId ? { tenantId } : {};
    return apiClient.post("/integrations/sync/job-roles", null, { params });
  },

  /**
   * Sync employees from HR provider.
   * @param {string} tenantId - Tenant ID (optional, uses current tenant)
   * @returns {Promise} API response with sync result
   */
  async syncEmployees(tenantId = null) {
    const params = tenantId ? { tenantId } : {};
    return apiClient.post("/integrations/sync/employees", null, { params });
  },

  /**
   * Sync clock records from HR provider.
   * @param {Object} options - Sync options
   * @param {string} options.tenantId - Tenant ID (optional)
   * @param {string} options.fromDate - Start date (ISO format)
   * @param {string} options.toDate - End date (ISO format)
   * @returns {Promise} API response with sync result
   */
  async syncClockRecords({ tenantId = null, fromDate = null, toDate = null } = {}) {
    const params = {};
    if (tenantId) params.tenantId = tenantId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient.post("/integrations/sync/clock-records", null, { params });
  },

  /**
   * Sync TRONC records from Till provider.
   * @param {Object} options - Sync options
   * @param {string} options.tenantId - Tenant ID (optional)
   * @param {string} options.fromDate - Start date (ISO format)
   * @param {string} options.toDate - End date (ISO format)
   * @returns {Promise} API response with sync result
   */
  async syncTroncRecords({ tenantId = null, fromDate = null, toDate = null } = {}) {
    const params = {};
    if (tenantId) params.tenantId = tenantId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return apiClient.post("/integrations/sync/tronc-records", null, { params });
  },

  /**
   * Full HR sync - syncs locations, job roles, then employees.
   * @param {string} tenantId - Tenant ID (optional, uses current tenant)
   * @returns {Promise} API response with full sync result
   */
  async fullHrSync(tenantId = null) {
    const params = tenantId ? { tenantId } : {};
    return apiClient.post("/integrations/sync/full-hr", null, { params });
  },

  // ========================================
  // PAYROLL EXPORT
  // ========================================

  /**
   * Export TRONC payroll data to the configured payroll provider (e.g. Xero).
   * Requires a Payroll integration to be configured.
   * @param {Object} options - Export options
   * @param {string} options.periodStart - Start date (ISO format)
   * @param {string} options.periodEnd - End date (ISO format)
   * @param {string} [options.tenantId] - Tenant ID (optional, global admin only)
   * @returns {Promise} API response with export result
   */
  async exportPayroll({ periodStart, periodEnd, tenantId = null } = {}) {
    const body = { periodStart, periodEnd };
    if (tenantId) body.tenantId = tenantId;
    return apiClient.post("/integrations/export/payroll", body);
  },
};

// Named export
export { integrationsApi };

// Default export
export default integrationsApi;
